import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {useNavigate} from "react-router-dom";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControlLabel,
    IconButton,
    Modal,
    Pagination,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DataObjectIcon from "@mui/icons-material/DataObject";

import {useAlert} from "../context/AlertContext";
import AppTableHeaderCell from "./AppTableHeaderCell";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import AppModalBox from "./AppModalBox";

import {deleteCompany, getCompanies, getCompanyById, updateCompany} from "../api/api";
import {Company} from "../types";

interface Props {
    onAdd: () => void;
    refreshTrigger: boolean;
    refreshCompanies: () => void;
}

const getInitials = (name?: string) => {
    if (!name || !name.trim()) return "?";

    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }

    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
};

const CompaniesList = ({onAdd, refreshTrigger, refreshCompanies}: Props) => {
    const {t} = useTranslation();
    const {showAlert} = useAlert();
    const navigate = useNavigate();

    const [companies, setCompanies] = useState<Company[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const pageSize = 10;

    const [loading, setLoading] = useState(false);

    const [companyToDelete, setCompanyToDelete] = useState<number | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [editingCompany, setEditingCompany] = useState<Company | null>(null);
    const [editModalLoading, setEditModalLoading] = useState(false);
    const [editSaving, setEditSaving] = useState(false);

    const [editName, setEditName] = useState("");
    const [editDescription, setEditDescription] = useState("");
    const [editMetadataSettings, setEditMetadataSettings] = useState("");
    const [editActive, setEditActive] = useState(true);

    const fetchCompaniesPage = async () => {
        try {
            setLoading(true);
            const {companies, totalPages} = await getCompanies(page - 1, pageSize);
            setCompanies(companies);
            setTotalPages(totalPages);
        } catch (error: any) {
            showAlert(error?.message ?? t("companieslist.errors.loadFailed"), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCompaniesPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, refreshTrigger]);

    const openEdit = async (company: Company) => {
        try {
            setEditingCompany(company);
            setEditModalLoading(true);

            const details = await getCompanyById(company.id);

            setEditName(details.name ?? "");
            setEditDescription(details.description ?? "");
            setEditMetadataSettings(details.metadataSettings ?? "");
            setEditActive(details.active);
        } catch (error: any) {
            showAlert(error?.message || t("companieslist.errors.loadDetailsFailed"), "error");
            setEditingCompany(null);
        } finally {
            setEditModalLoading(false);
        }
    };

    const closeEditModal = () => {
        setEditingCompany(null);
        setEditModalLoading(false);
        setEditSaving(false);
        setEditName("");
        setEditDescription("");
        setEditMetadataSettings("");
        setEditActive(true);
    };

    const handleEditCompany = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!editingCompany) return;

        if (!editName.trim()) {
            showAlert(t("companieslist.errors.nameRequired"), "error");
            return;
        }

        try {
            setEditSaving(true);

            await updateCompany(editingCompany.id, {
                name: editName.trim(),
                description: editDescription.trim() || null,
                metadataSettings: editMetadataSettings.trim() || null,
                active: editActive,
            });

            showAlert(t("companieslist.alerts.updated"), "success");
            closeEditModal();
            refreshCompanies();
        } catch (error: any) {
            showAlert(error?.message || t("companieslist.errors.updateFailed"), "error");
        } finally {
            setEditSaving(false);
        }
    };

    const handleDeleteCompany = async () => {
        if (!companyToDelete) return;

        try {
            setDeleteLoading(true);
            await deleteCompany(companyToDelete);

            showAlert(t("companieslist.alerts.deleted"), "success");
            setCompanyToDelete(null);
            refreshCompanies();
        } catch (error: any) {
            showAlert(error?.message || t("companieslist.errors.deleteFailed"), "error");
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <>
            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", py: 8}}>
                    <CircularProgress/>
                </Box>
            ) : (
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        borderRadius: 3,
                        overflow: "hidden",
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                    })}
                >
                    <Box
                        sx={(theme) => ({
                            display: "flex",
                            alignItems: {xs: "stretch", sm: "center"},
                            gap: 1.5,
                            px: {xs: 1.5, md: 3},
                            py: 2,
                            flexDirection: {xs: "column", sm: "row"},
                            backgroundColor: theme.palette.background.paper,
                            borderBottom: `1px solid ${theme.palette.action.hover}`,
                        })}
                    >
                        <Box sx={{minWidth: 0}}>
                            <Typography variant="h6" sx={{fontWeight: 900, lineHeight: 1.2}}>
                                {t("companieslist.title")}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                {t("companieslist.subtitle")}
                            </Typography>
                        </Box>

                        <Box sx={{flexGrow: 1, display: {xs: "none", sm: "block"}}}/>

                        <Box
                            sx={(theme) => ({
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                width: {xs: "100%", sm: "auto"},
                                px: {xs: 1.25, sm: 0},
                                py: {xs: 1, sm: 0},
                                borderRadius: {xs: 2.5, sm: 0},
                                backgroundColor: {
                                    xs: theme.palette.action.hover,
                                    sm: "transparent",
                                },
                            })}
                        >
                            <Typography variant="body2" color="text.secondary" sx={{fontWeight: 700}}>
                                {t("common.pageOf", {page, totalPages})}
                            </Typography>

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{fontWeight: 700, display: {xs: "block", sm: "none"}}}
                            >
                                {t("companieslist.shown", {count: companies.length})}
                            </Typography>
                        </Box>

                        <IconButton
                            onClick={onAdd}
                            aria-label={t("companieslist.addCompany")}
                            sx={(theme) => ({
                                width: 42,
                                height: 42,
                                borderRadius: "50%",
                                backgroundColor: theme.palette.action.hover,
                                color: theme.palette.text.primary,
                                display: {xs: "none", sm: "inline-flex"},
                                "&:hover": {
                                    backgroundColor: theme.palette.action.selected,
                                },
                            })}
                        >
                            <AddIcon fontSize="small"/>
                        </IconButton>

                        <Button
                            onClick={onAdd}
                            variant="contained"
                            startIcon={<AddBusinessIcon fontSize="small"/>}
                            sx={{
                                display: {xs: "inline-flex", sm: "none"},
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 800,
                                py: 0.95,
                            }}
                        >
                            {t("companieslist.addCompany")}
                        </Button>
                    </Box>

                    {companies.length === 0 ? (
                        <Box sx={{py: 10, px: 3, textAlign: "center"}}>
                            <Typography variant="h6" sx={{mb: 1}}>
                                {t("companieslist.noCompaniesFound")}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                {t("companieslist.noCompaniesDescription")}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Desktop table */}
                            <Box sx={{display: {xs: "none", md: "block"}, width: "100%", overflowX: "auto"}}>
                                <table style={{width: "100%", borderCollapse: "separate", borderSpacing: 0}}>
                                    <thead>
                                    <tr style={{background: "#fafafa"}}>
                                        <AppTableHeaderCell>
                                            {t("companieslist.company")}
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell>
                                            {t("companieslist.description")}
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell>
                                            {t("companieslist.status")}
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell>
                                            {t("companieslist.metadata")}
                                        </AppTableHeaderCell>

                                        <AppTableHeaderCell align="right">
                                            {t("common.actions")}
                                        </AppTableHeaderCell>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {companies.map((company) => (
                                        <tr
                                            key={company.id}
                                            style={{
                                                transition: "background-color 0.2s ease",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = "#fafafa";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = "transparent";
                                            }}
                                        >
                                            <td style={{padding: "18px 16px"}}>
                                                <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                                    <Box
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: "50%",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontSize: 13,
                                                            fontWeight: 700,
                                                            bgcolor: "grey.300",
                                                            color: "text.primary",
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {getInitials(company.name)}
                                                    </Box>

                                                    <Box sx={{minWidth: 0}}>
                                                        <Typography variant="body2" sx={{fontWeight: 700}} noWrap>
                                                            {company.name || "—"}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </td>

                                            <td style={{padding: "18px 16px", maxWidth: 360}}>
                                                <Typography variant="body2" color="text.secondary" noWrap>
                                                    {company.description || "—"}
                                                </Typography>
                                            </td>

                                            <td style={{padding: "18px 16px"}}>
                                                <Chip
                                                    size="small"
                                                    label={company.active ? t("companieslist.active") : t("companieslist.inactive")}
                                                    color={company.active ? "success" : "default"}
                                                    variant={company.active ? "filled" : "outlined"}
                                                />
                                            </td>

                                            <td style={{padding: "18px 16px", maxWidth: 260}}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{fontFamily: "monospace"}}
                                                    noWrap
                                                >
                                                    {company.metadataSettings || "—"}
                                                </Typography>
                                            </td>

                                            <td style={{padding: "18px 16px", textAlign: "right"}}>
                                                <Box sx={{display: "inline-flex", gap: 1}}>
                                                    <Tooltip title={t("companieslist.details")}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => navigate(`/companies/${company.id}`)}
                                                            aria-label={t("companieslist.details")}
                                                            sx={(theme) => ({
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: "50%",
                                                                backgroundColor: theme.palette.action.hover,
                                                                color: theme.palette.text.primary,
                                                                "&:hover": {
                                                                    backgroundColor: theme.palette.action.selected,
                                                                },
                                                            })}
                                                        >
                                                            <VisibilityIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title={t("common.edit")}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openEdit(company)}
                                                            aria-label={t("common.edit")}
                                                            sx={(theme) => ({
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: "50%",
                                                                backgroundColor: theme.palette.action.hover,
                                                                color: theme.palette.text.primary,
                                                                "&:hover": {
                                                                    backgroundColor: theme.palette.action.selected,
                                                                },
                                                            })}
                                                        >
                                                            <EditIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>

                                                    <Tooltip title={t("common.delete")}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setCompanyToDelete(company.id)}
                                                            aria-label={t("common.delete")}
                                                            sx={(theme) => ({
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: "50%",
                                                                backgroundColor: theme.palette.action.hover,
                                                                color: theme.palette.text.primary,
                                                                "&:hover": {
                                                                    backgroundColor: theme.palette.action.selected,
                                                                },
                                                            })}
                                                        >
                                                            <DeleteIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </Box>

                            {/* Mobile cards */}
                            <Box
                                sx={{
                                    display: {xs: "flex", md: "none"},
                                    flexDirection: "column",
                                    gap: 1.25,
                                    p: 1.5,
                                }}
                            >
                                <Box
                                    sx={(theme) => ({
                                        p: 1.5,
                                        borderRadius: 3,
                                        background:
                                            theme.palette.mode === "dark"
                                                ? "linear-gradient(135deg, rgba(30,41,59,0.95), rgba(15,23,42,0.95))"
                                                : "linear-gradient(135deg, rgba(248,250,252,1), rgba(241,245,249,1))",
                                        border: `1px solid ${theme.palette.divider}`,
                                    })}
                                >
                                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1}}>
                                        <Box sx={{minWidth: 0}}>
                                            <Typography sx={{fontWeight: 900, lineHeight: 1.2}}>
                                                {t("companieslist.companyManagement")}
                                            </Typography>

                                            <Typography variant="caption" color="text.secondary">
                                                {t("companieslist.companyManagementDescription")}
                                            </Typography>
                                        </Box>

                                        <Chip
                                            size="small"
                                            label={t("companieslist.shown", {count: companies.length})}
                                            sx={(theme) => ({
                                                fontWeight: 800,
                                                backgroundColor: theme.palette.action.hover,
                                                flexShrink: 0,
                                            })}
                                        />
                                    </Box>
                                </Box>

                                {companies.map((company) => (
                                    <Box
                                        key={company.id}
                                        sx={(theme) => ({
                                            p: 1.5,
                                            borderRadius: 3,
                                            border: `1px solid ${theme.palette.divider}`,
                                            backgroundColor: theme.palette.background.paper,
                                            boxShadow:
                                                theme.palette.mode === "dark"
                                                    ? "0 14px 32px rgba(0,0,0,0.32)"
                                                    : "0 14px 32px rgba(15,23,42,0.07)",
                                            overflow: "hidden",
                                            position: "relative",
                                            "&:before": {
                                                content: '""',
                                                position: "absolute",
                                                top: 0,
                                                bottom: 0,
                                                left: 0,
                                                width: 4,
                                                backgroundColor: company.active
                                                    ? theme.palette.success.main
                                                    : theme.palette.text.disabled,
                                            },
                                        })}
                                    >
                                        <Box sx={{display: "flex", alignItems: "flex-start", gap: 1.25}}>
                                            <Box
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: "50%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: 14,
                                                    fontWeight: 900,
                                                    bgcolor: "grey.300",
                                                    color: "text.primary",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {getInitials(company.name)}
                                            </Box>

                                            <Box sx={{minWidth: 0, flexGrow: 1}}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "flex-start",
                                                        justifyContent: "space-between",
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontWeight: 900,
                                                            lineHeight: 1.35,
                                                            wordBreak: "break-word",
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        {company.name || "—"}
                                                    </Typography>

                                                    <Chip
                                                        size="small"
                                                        label={company.active ? t("companieslist.active") : t("companieslist.inactive")}
                                                        color={company.active ? "success" : "default"}
                                                        variant={company.active ? "filled" : "outlined"}
                                                        sx={{
                                                            flexShrink: 0,
                                                            fontWeight: 800,
                                                            height: 24,
                                                        }}
                                                    />
                                                </Box>

                                                <Box
                                                    sx={(theme) => ({
                                                        mt: 1,
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: 0.5,
                                                        px: 1,
                                                        py: 0.45,
                                                        borderRadius: 999,
                                                        backgroundColor: theme.palette.action.hover,
                                                    })}
                                                >
                                                    <BusinessIcon
                                                        sx={{
                                                            fontSize: 15,
                                                            color: "text.secondary",
                                                        }}
                                                    />

                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{fontWeight: 800}}
                                                    >
                                                        {t("companieslist.companyId")}: {company.id}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={(theme) => ({
                                                mt: 1.5,
                                                p: 1.25,
                                                borderRadius: 2.5,
                                                backgroundColor: theme.palette.action.hover,
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 1,
                                            })}
                                        >
                                            <Box sx={{display: "flex", alignItems: "flex-start", gap: 1}}>
                                                <DescriptionOutlinedIcon
                                                    sx={{
                                                        fontSize: 18,
                                                        color: "text.secondary",
                                                        mt: 0.15,
                                                        flexShrink: 0,
                                                    }}
                                                />

                                                <Box sx={{minWidth: 0}}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{fontWeight: 800, display: "block"}}
                                                    >
                                                        {t("companieslist.description")}
                                                    </Typography>

                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 700,
                                                            wordBreak: "break-word",
                                                            display: "-webkit-box",
                                                            WebkitLineClamp: 3,
                                                            WebkitBoxOrient: "vertical",
                                                            overflow: "hidden",
                                                        }}
                                                    >
                                                        {company.description || "—"}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Box sx={{display: "flex", alignItems: "flex-start", gap: 1}}>
                                                <DataObjectIcon
                                                    sx={{
                                                        fontSize: 18,
                                                        color: "text.secondary",
                                                        mt: 0.15,
                                                        flexShrink: 0,
                                                    }}
                                                />

                                                <Box sx={{minWidth: 0, flexGrow: 1}}>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{fontWeight: 800, display: "block"}}
                                                    >
                                                        {t("companieslist.metadata")}
                                                    </Typography>

                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            fontFamily:
                                                                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                                            display: "-webkit-box",
                                                            WebkitLineClamp: 3,
                                                            WebkitBoxOrient: "vertical",
                                                            overflow: "hidden",
                                                            wordBreak: "break-word",
                                                        }}
                                                    >
                                                        {company.metadataSettings || "—"}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={{
                                                mt: 1.5,
                                                display: "grid",
                                                gridTemplateColumns: "1fr 1fr",
                                                gap: 1,
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => navigate(`/companies/${company.id}`)}
                                                startIcon={<VisibilityIcon fontSize="small"/>}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: "none",
                                                    fontWeight: 800,
                                                    py: 0.85,
                                                }}
                                            >
                                                {t("companieslist.details")}
                                            </Button>

                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => openEdit(company)}
                                                startIcon={<EditIcon fontSize="small"/>}
                                                sx={{
                                                    borderRadius: 2,
                                                    textTransform: "none",
                                                    fontWeight: 800,
                                                    py: 0.85,
                                                }}
                                            >
                                                {t("common.edit")}
                                            </Button>
                                        </Box>

                                        <Button
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            onClick={() => setCompanyToDelete(company.id)}
                                            startIcon={<DeleteIcon fontSize="small"/>}
                                            sx={{
                                                mt: 1,
                                                borderRadius: 2,
                                                textTransform: "none",
                                                fontWeight: 800,
                                                py: 0.85,
                                            }}
                                        >
                                            {t("companieslist.deleteCompany")}
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        </>
                    )}
                </Paper>
            )}

            <Box sx={{display: "flex", justifyContent: "center", mt: 3}}>
                <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    disabled={loading}
                />
            </Box>

            <DeleteConfirmationModal
                open={companyToDelete !== null}
                onClose={() => (deleteLoading ? null : setCompanyToDelete(null))}
                onConfirm={handleDeleteCompany}
            />

            <Modal open={editingCompany !== null} onClose={closeEditModal}>
                <AppModalBox
                    sx={{
                        p: {xs: 2, sm: 3, md: 4},
                        margin: "auto",
                        width: {xs: "calc(100vw - 24px)", sm: 540},
                        maxWidth: "95vw",
                        maxHeight: "95vh",
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Box sx={{display: "flex", alignItems: "center", gap: 1.25}}>
                        <Box
                            sx={(theme) => ({
                                width: 42,
                                height: 42,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 13,
                                fontWeight: 900,
                                bgcolor: theme.palette.action.hover,
                                color: theme.palette.text.primary,
                                flexShrink: 0,
                            })}
                        >
                            {editingCompany ? getInitials(editingCompany.name) : "?"}
                        </Box>

                        <Box sx={{minWidth: 0}}>
                            <Typography variant="h6" sx={{fontWeight: 900, lineHeight: 1.2}} noWrap>
                                {t("companieslist.editCompany")}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" noWrap>
                                {editingCompany?.name ?? t("companieslist.updateCompanyDetails")}
                            </Typography>
                        </Box>
                    </Box>

                    {editModalLoading ? (
                        <Box sx={{display: "flex", justifyContent: "center", py: 4}}>
                            <CircularProgress/>
                        </Box>
                    ) : (
                        <form onSubmit={handleEditCompany}>
                            <TextField
                                label={t("companieslist.name")}
                                fullWidth
                                required
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                disabled={editSaving}
                                sx={{mb: 2}}
                            />

                            <TextField
                                label={t("companieslist.description")}
                                fullWidth
                                multiline
                                minRows={3}
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                disabled={editSaving}
                                sx={{mb: 2}}
                            />

                            <TextField
                                label={t("companieslist.metadataSettingsJson")}
                                fullWidth
                                multiline
                                minRows={4}
                                value={editMetadataSettings}
                                onChange={(e) => setEditMetadataSettings(e.target.value)}
                                disabled={editSaving}
                                sx={{mb: 2}}
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={editActive}
                                        onChange={(e) => setEditActive(e.target.checked)}
                                        disabled={editSaving}
                                    />
                                }
                                label={t("companieslist.active")}
                                sx={{mb: 2}}
                            />

                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    justifyContent: "flex-end",
                                    flexDirection: {xs: "column-reverse", sm: "row"},
                                }}
                            >
                                <Button
                                    onClick={closeEditModal}
                                    disabled={editSaving}
                                    sx={{
                                        textTransform: "none",
                                        width: {xs: "100%", sm: "auto"},
                                    }}
                                >
                                    {t("common.cancel")}
                                </Button>

                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={editSaving}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 800,
                                        width: {xs: "100%", sm: "auto"},
                                    }}
                                >
                                    {editSaving ? t("companieslist.saving") : t("companieslist.saveChanges")}
                                </Button>
                            </Box>
                        </form>
                    )}
                </AppModalBox>
            </Modal>
        </>
    );
};

export default CompaniesList;